import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '@/config/gameConfig';
import { THEME } from '@/config/theme';
import { resetLocalProgress } from '@/config/progress';
import { captureError } from '@/sentry';
import { drawArcadeBackground, addTextGlow } from '@/utils/arcadeBackground';
import { isMobileDevice } from '@/utils/mobile';
import { AudioManager } from '@/systems/AudioManager';
import { fetchServerVersion, formatVersionText, getClientVersion } from '@/utils/version';
import { resetRemoteProgress } from '@/services/progressSync';
import { isSupabaseConfigured, onAuthChange } from '@/services/supabaseClient';
import { fetchAccountStatus, shouldOfferSaveProgress } from '@/services/accountStatus';
import { getDiscordGuildId, setDiscordIdlePresence, onDiscordAuthChange, getDiscordDisplayName } from '@/discord';
import { mountAccountBadge } from '@/ui/accountBadge';
import { isDiscordActivity } from '@/utils/discordActivity';
import { createButton } from '@/ui/buttons';
import { SaveProgressModal, createConfirmationModal } from '@/ui/Modal';
import { ensureInstanceLobbyRoom, getInstanceLobbyState, claimInstanceLobby } from '@/online/instanceLobby';
import { getOnlineClient, resetOnlineClient } from '@/online/colyseusClient';
import { setOnlineRoom } from '@/online/session';
import { isDevMode } from '@/online/debug';
import { getDeviceId } from '@/online/deviceId';
import { trackMenuView, trackModeSelect } from '@/services/analytics';

const MENU_PRIMARY_BUTTON_SIZE = {
  width: 250,
  height: 60,
  fontSize: THEME.sizes.bodyLarge,
};
const MENU_SECONDARY_BUTTON_SIZE = {
  width: 180,
  height: 45,
  fontSize: THEME.sizes.body,
};
const MENU_DANGER_BUTTON_SIZE = {
  width: 100,
  height: 30,
  fontSize: THEME.sizes.tiny,
};

export class MenuScene extends Phaser.Scene {
  private saveModal: SaveProgressModal | null = null;
  private saveDevButton: Phaser.GameObjects.Container | null = null;
  private resetDevButton: Phaser.GameObjects.Container | null = null;
  private versionLabel: Phaser.GameObjects.Text | null = null;
  private devModeListener: (() => void) | null = null;

  constructor() {
    super({ key: 'MenuScene' });
  }

  private openSaveModal(): void {
    if (this.saveModal) return;
    this.saveModal = new SaveProgressModal({
      scene: this,
      onDestroy: () => { this.saveModal = null; }
    });
  }

  private setButtonEnabled(btn: Phaser.GameObjects.Container, enabled: boolean): void {
    const bg = btn.list[0] as Phaser.GameObjects.Rectangle | undefined;
    if (bg) {
      if (enabled) bg.setInteractive({ useHandCursor: true });
      else bg.disableInteractive();
    }
    btn.setAlpha(enabled ? 1 : 0.5);
  }

  private setSaveDevEnabled(enabled: boolean): void {
    if (!this.saveDevButton) return;
    this.setButtonEnabled(this.saveDevButton, enabled);
  }

  private async refreshSaveButtonState(): Promise<void> {
    fetchAccountStatus()
      .then((status) => {
        this.setSaveDevEnabled(shouldOfferSaveProgress(status));
      })
      .catch((error) => {
        captureError(error, { scope: 'menu', action: 'refreshSaveButtonState' });
        this.setSaveDevEnabled(false);
      });
  }

  private updateDevModeUI(): void {
    const enabled = isDevMode();

    // Version label
    if (enabled && !this.versionLabel) {
      this.renderVersionLine();
    } else if (!enabled && this.versionLabel) {
      this.versionLabel.destroy();
      this.versionLabel = null;
    }

    // Save/Reset buttons
    if (enabled && !this.saveDevButton && !isMobileDevice()) {
      this.saveDevButton = createButton(this, {
        x: GAME_WIDTH - 100,
        y: GAME_HEIGHT - 80,
        text: 'SAVE',
        onClick: () => this.openSaveModal(),
        style: 'danger',
        buttonType: 'normal',
        sizes: { danger: MENU_DANGER_BUTTON_SIZE },
      });

      this.resetDevButton = createButton(this, {
        x: GAME_WIDTH - 100,
        y: GAME_HEIGHT - 40,
        text: 'RESET',
        onClick: () => this.showResetConfirmation(),
        style: 'danger',
        buttonType: 'normal',
        sizes: { danger: MENU_DANGER_BUTTON_SIZE },
      });
    } else if (!enabled && this.saveDevButton) {
      this.saveDevButton.destroy();
      this.saveDevButton = null;
      this.resetDevButton?.destroy();
      this.resetDevButton = null;
    }
  }

  shutdown(): void {
    if (this.devModeListener) {
      window.removeEventListener('devmodechange', this.devModeListener);
      this.devModeListener = null;
    }
  }

  create(): void {
    // Fade in
    this.cameras.main.fadeIn(300, 5, 7, 22);

    // Track menu view
    trackMenuView();

    // Discord Activity presence (menus/idle)
    setDiscordIdlePresence();

    // Start ambient music if not already playing
    this.startAmbientMusic();

    // Arcade background
    drawArcadeBackground(this);

    // Hero panel (keeps structure without the triangle shards)
    const panel = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 20, 760, 560, THEME.colors.backgroundLightHex, 0.8);
    panel.setStrokeStyle(3, THEME.colors.magentaHex);
    panel.setDepth(-1);

    // Title with glow
    const title = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 3, 'MORSE CODE HERO', {
      fontFamily: THEME.fonts.heading,
      fontSize: '56px',
      color: THEME.colors.cyan,
    });
    title.setOrigin(0.5);
    addTextGlow(title, THEME.colors.cyan, 20);

    // Subtitle
    const subtitle = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 3 + 60, 'DOT · DASH · DOMINATE', {
      fontFamily: THEME.fonts.body,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.textMuted,
    });
    subtitle.setOrigin(0.5);

    const modeStartY = GAME_HEIGHT / 2 - 20;
    const modeStep = 65;

    const isMobile = isMobileDevice();
    const showLeaderboard = !isMobile && isDiscordActivity() && Boolean(getDiscordGuildId());
    if (isMobile) {
      const mobileHint = isDiscordActivity()
        ? 'Join the voice channel • Gameplay requires landscape'
        : 'Mobile joiners only • Gameplay requires landscape';
      this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 3 + 92, mobileHint, {
        fontFamily: THEME.fonts.pixel,
        fontSize: THEME.sizes.small,
        color: THEME.colors.textMuted,
      }).setOrigin(0.5);
    }

    // Mode buttons (magenta - primary actions)
    if (!isMobile) {
      createButton(this, {
        x: GAME_WIDTH / 2,
        y: modeStartY,
        text: 'SINGLE PLAYER',
        onClick: () => {
          trackModeSelect('solo');
          this.transitionTo('LevelSelectScene');
        },
        style: 'primary',
        buttonType: 'other',
        sizes: { primary: MENU_PRIMARY_BUTTON_SIZE },
      });

      createButton(this, {
        x: GAME_WIDTH / 2,
        y: modeStartY + modeStep,
        text: 'DAILY CHALLENGE',
        onClick: () => {
          trackModeSelect('daily');
          this.transitionTo('DailyChallengeScene');
        },
        style: 'primary',
        buttonType: 'normal',
        sizes: { primary: MENU_PRIMARY_BUTTON_SIZE },
      });

      createButton(this, {
        x: GAME_WIDTH / 2,
        y: modeStartY + modeStep * 2,
        text: 'FRIEND PLAY',
        onClick: () => {
          trackModeSelect('coop');
          if (isDiscordActivity()) {
            void this.handleDiscordFriendPlay();
          } else {
            this.transitionTo('FriendWebPlayScene');
          }
        },
        style: 'primary',
        buttonType: 'normal',
        sizes: { primary: MENU_PRIMARY_BUTTON_SIZE },
      });

      const leaderboardYDesktop = modeStartY + modeStep * 3;
      const settingsYDesktop = modeStartY + modeStep * (showLeaderboard ? 4 : 3);
      const creditsYDesktop = modeStartY + modeStep * (showLeaderboard ? 5 : 4);

      if (showLeaderboard) {
        createButton(this, {
          x: GAME_WIDTH / 2,
          y: leaderboardYDesktop,
          text: 'LEADERBOARDS',
          onClick: () => this.transitionTo('LeaderboardScene'),
          style: 'secondary',
          buttonType: 'normal',
          sizes: { secondary: MENU_SECONDARY_BUTTON_SIZE },
        });
      }
      createButton(this, {
        x: GAME_WIDTH / 2,
        y: settingsYDesktop,
        text: 'SETTINGS',
        onClick: () => this.transitionTo('SettingsScene'),
        style: 'secondary',
        buttonType: 'normal',
        sizes: { secondary: MENU_SECONDARY_BUTTON_SIZE },
      });

      createButton(this, {
        x: GAME_WIDTH / 2,
        y: creditsYDesktop,
        text: 'CREDITS',
        onClick: () => this.transitionTo('CreditsScene'),
        style: 'secondary',
        buttonType: 'normal',
        sizes: { secondary: MENU_SECONDARY_BUTTON_SIZE },
      });
    }

    if (isMobile) {
      const mobileButtonsY = GAME_HEIGHT / 2 + 40;
      const joinY = mobileButtonsY;
      const settingsY = mobileButtonsY + 75;
      const creditsY = mobileButtonsY + 150;

      createButton(this, {
        x: GAME_WIDTH / 2,
        y: joinY,
        text: 'JOIN GAME',
        onClick: () => {
          if (isDiscordActivity()) {
            void this.handleDiscordFriendPlay();
          } else {
            this.transitionTo('JoinScene');
          }
        },
        style: 'primary',
        buttonType: 'normal',
        sizes: { primary: MENU_PRIMARY_BUTTON_SIZE },
      });

      createButton(this, {
        x: GAME_WIDTH / 2,
        y: settingsY,
        text: 'SETTINGS',
        onClick: () => this.transitionTo('SettingsScene'),
        style: 'secondary',
        buttonType: 'normal',
        sizes: { secondary: MENU_SECONDARY_BUTTON_SIZE },
      });

      createButton(this, {
        x: GAME_WIDTH / 2,
        y: creditsY,
        text: 'CREDITS',
        onClick: () => this.transitionTo('CreditsScene'),
        style: 'secondary',
        buttonType: 'normal',
        sizes: { secondary: MENU_SECONDARY_BUTTON_SIZE },
      });
    }

    // Dev mode UI elements - reactive to dev mode toggle
    this.updateDevModeUI();
    this.devModeListener = () => this.updateDevModeUI();
    window.addEventListener('devmodechange', this.devModeListener);

    // Auth status indicator (uses shared component with auth change listeners)
    mountAccountBadge(this);

    // Save button state (separate from display)
    this.refreshSaveButtonState();
    if (isDiscordActivity()) {
      onDiscordAuthChange(() => this.refreshSaveButtonState());
    }
    if (isSupabaseConfigured()) {
      onAuthChange(() => this.refreshSaveButtonState());
    }

    // Keybinding hint removed (bindings vary by mode and are configurable)

    // Animate title
    this.tweens.add({
      targets: title,
      y: title.y - 10,
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private transitionTo(scene: string, data?: object): void {
    this.cameras.main.fadeOut(200, 5, 7, 22);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start(scene, data);
    });
  }

  private async handleDiscordFriendPlay(): Promise<void> {
    // 1. Ensure instance lobby room is connected
    const instanceRoom = await ensureInstanceLobbyRoom();
    if (!instanceRoom) {
      // TODO: Show error toast
      return;
    }

    const state = getInstanceLobbyState();

    // 2. If room already live, join as guest
    if (state.status === 'live' && state.roomId) {
      try {
        const client = getOnlineClient();
        const room = await client.joinById(state.roomId, {
          name: getDiscordDisplayName() ?? 'Player',
          deviceId: getDeviceId(),
        });
        setOnlineRoom(room);
        this.scene.start('LobbyScene', { role: 'guest' });
        return;
      } catch (err) {
        // Room doesn't exist anymore (stale) - fall through to claim as host
        resetOnlineClient();
      }
    }

    // 3. Try to claim host
    const outcome = await claimInstanceLobby({ mode: 'coop' });

    if (outcome === 'claimed') {
      // We're the host - LobbyScene will create room and publish
      this.scene.start('LobbyScene', { role: 'host', mode: 'coop' });
    } else if (outcome === 'denied') {
      // Someone else claimed while we were trying - check state again
      const newState = getInstanceLobbyState();
      if (newState.status === 'live' && newState.roomId) {
        try {
          const client = getOnlineClient();
          const room = await client.joinById(newState.roomId, {
            name: getDiscordDisplayName() ?? 'Player',
            deviceId: getDeviceId(),
          });
          setOnlineRoom(room);
          this.scene.start('LobbyScene', { role: 'guest' });
        } catch (err) {
          // Room doesn't exist - try claiming again
          resetOnlineClient();
          const retryOutcome = await claimInstanceLobby({ mode: 'coop' });
          if (retryOutcome === 'claimed') {
            this.scene.start('LobbyScene', { role: 'host', mode: 'coop' });
          }
        }
      }
    }
    // 'unavailable' - silently fail (TODO: show error)
  }

  private showResetConfirmation(): void {
    createConfirmationModal({
      scene: this,
      title: 'RESET PROGRESS?',
      message: 'All levels and high scores\nwill be cleared.',
      confirmText: 'RESET',
      onConfirm: async () => {
        try {
          await resetRemoteProgress();
          resetLocalProgress();
        } catch (err) {
          if (err instanceof Error) {
            captureError(err, { source: 'reset_progress' });
          }
        }
      },
    });
  }

  private startAmbientMusic(): void {
    // Check if ambient music is already playing (persists across scenes)
    const existingMusic = this.sound.get('ambient');
    if (existingMusic && existingMusic.isPlaying) {
      return;
    }

    const audioManager = AudioManager.get(this);

    // If audio context is locked, wait for user interaction
    if (this.sound.locked) {
      this.sound.once('unlocked', () => {
        audioManager.playMusic('ambient');
      });
    } else {
      audioManager.playMusic('ambient');
    }
  }

  private renderVersionLine(): void {
    const clientVersion = getClientVersion();
    this.versionLabel = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 18, formatVersionText(clientVersion, null), {
      fontFamily: THEME.fonts.body,
      fontSize: THEME.sizes.tiny,
      color: THEME.colors.textMuted,
    });
    this.versionLabel.setOrigin(0.5);
    this.versionLabel.setAlpha(0.8);

    const menuSceneKey = this.scene.key;
    fetchServerVersion().then((serverVersion) => {
      if (!serverVersion) return;
      if (!this.scene.isActive(menuSceneKey)) return;
      this.versionLabel?.setText(formatVersionText(clientVersion, serverVersion));
    });
  }
}
